import React, { useEffect, useState, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../store';
import { setTracks, removeTrack, setRenders, removeRenderJob, setSyncs, removeSyncStatus } from '../store/tracksSlice';
import { logout, updateUser } from '../store/authSlice';
import { useNavigate, useLocation } from 'react-router-dom';
import { Map as MapIcon, Plus, Trash2, Video, Download, X } from 'lucide-react';
import { apiGet, apiDelete, apiPost, apiPatch, API_BASE } from '../utils/api';
import { getEnv } from '../utils/env';
import {
  formatDate as formatGlobalDate,
  formatDistance as formatGlobalDistance,
  formatElevation as formatGlobalElevation,
  formatDuration as formatGlobalDuration,
  formatSpeedOrPace as formatGlobalSpeedOrPace,
} from '../utils/formatters';

import { useUI } from '../context/UIContext';
import { Header } from '../components/library/Header';
import { QuotaBar } from '../components/library/QuotaBar';
import { FiltersPanel } from '../components/library/FiltersPanel';
import { TrackCard } from '../components/library/TrackCard';
import { SettingsModal } from '../components/library/SettingsModal';
import { Modal } from '../components/Modal';

interface QuotaDetails {
  used: {
    '720p': number;
    '1080p': number;
    '4K': number;
    'satellite'?: number;
  };
  limits: {
    '720p': number;
    '1080p': number;
    '4K': number;
    'satellite'?: number;
  };
}

export const TrackLibrary: React.FC = () => {
  const token = useAppSelector((state) => state.auth.token);
  const user = useAppSelector((state) => state.auth.user);
  const tracks = useAppSelector((state) => state.tracks.tracks);
  const renders = useAppSelector((state) => state.tracks.renders);
  const syncs = useAppSelector((state) => state.tracks.syncs);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const ui = useUI();

  const stravaSync = syncs['strava'];
  const syncingStrava = stravaSync?.status === 'fetching' || stravaSync?.status === 'syncing';

  const getFullVideoUrl = (path?: string | null) => {
    if (!path) return '';
    if (path.startsWith('http')) return path;
    const fullPath = path.startsWith('/') ? path : `/${path}`;
    return `${API_BASE}${fullPath}${fullPath.includes('?') ? '&' : '?'}token=${token}`;
  };

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [quota, setQuota] = useState<QuotaDetails | null>(null);

  // Pagination & Aggregates State
  const [aggregates, setAggregates] = useState<{
    totalDistance: number;
    totalElevationGain: number;
    totalDuration: number;
  } | null>(() => {
    const saved = sessionStorage.getItem('library_aggregates');
    try {
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });
  const [totalCount, setTotalCount] = useState(() => {
    return parseInt(sessionStorage.getItem('library_totalCount') || '0', 10);
  });
  const [page, setPage] = useState(() => {
    return parseInt(sessionStorage.getItem('library_page') || '1', 10);
  });
  const [hasMore, setHasMore] = useState(() => {
    return sessionStorage.getItem('library_hasMore') === 'true';
  });
  const [fetchingMore, setFetchingMore] = useState(false);

  // Filter & Search States
  const [searchQuery, setSearchQuery] = useState(() => sessionStorage.getItem('library_search') || '');
  const [debouncedSearch, setDebouncedSearch] = useState(() => sessionStorage.getItem('library_search') || '');
  const [selectedActivityType, setSelectedActivityType] = useState(() => sessionStorage.getItem('library_activityType') || 'all');
  const [selectedDistanceRange, setSelectedDistanceRange] = useState(() => sessionStorage.getItem('library_distanceRange') || 'all');
  const [selectedDateRange, setSelectedDateRange] = useState(() => sessionStorage.getItem('library_dateRange') || 'all');
  const [sortByField, setSortByField] = useState(() => sessionStorage.getItem('library_sortBy') || 'startTime');
  const [sortOrderDirection, setSortOrderDirection] = useState<'ASC' | 'DESC'>(() => {
    return (sessionStorage.getItem('library_sortOrder') as 'ASC' | 'DESC') || 'DESC';
  });

  // Bulk Actions & Hover States
  const [selectedTrackIds, setSelectedTrackIds] = useState<string[]>([]);
  const [hoveredTrackId, setHoveredTrackId] = useState<string | null>(null);
  const [editingTrackId, setEditingTrackId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState<string>('');

  // Search Debouncing
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 450);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // External integrations connection states
  const [connections, setConnectionsState] = useState<
    Array<{ provider: string; linked: boolean; lastSyncAt: Date | null }>
  >([]);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [settingsActiveTab, setSettingsActiveTab] = useState<'profile' | 'accounts' | 'activities' | 'billing'>('profile');
  const [billingInterval, setBillingInterval] = useState<'month' | 'year'>('year');
  const [loadingBilling, setLoadingBilling] = useState(false);

  const fetchConnections = async () => {
    try {
      const data = await apiGet('/api/integrations/connections');
      setConnectionsState(data);
    } catch (err) {
      console.error('Failed to fetch connections:', err);
    }
  };

  const handleStravaConnect = () => {
    const clientId = getEnv('VITE_STRAVA_CLIENT_ID', 'mockclientid');
    const redirectUri = window.location.origin + '/auth/strava/callback';
    window.location.href = `https://www.strava.com/oauth/authorize?client_id=${clientId}&redirect_uri=${encodeURIComponent(
      redirectUri,
    )}&response_type=code&scope=activity:read_all&state=link`;
  };

  const handleStravaSync = async (range?: string) => {
    const rangeParam = typeof range === 'string' ? range : undefined;
    try {
      await apiPost('/api/integrations/sync/strava', { range: rangeParam });
      ui.toast('Strava synchronization started in background.', 'info');
    } catch (err: any) {
      ui.toast(err.message || 'Failed to start Strava sync', 'error');
    }
  };

  // Modals and UI interactive states
  const [playingVideoUrl, setPlayingVideoUrl] = useState<string | null>(null);

  const startEditing = (trackId: string, currentName: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingTrackId(trackId);
    setEditingName(currentName);
  };

  const cancelEditing = () => {
    setEditingTrackId(null);
    setEditingName('');
  };

  const saveName = async (trackId: string) => {
    if (!editingName.trim()) return;
    try {
      const updatedTrack = await apiPatch(`/api/tracks/${trackId}`, { name: editingName.trim() });
      const updatedTracks = tracks.map((t) => (t.id === trackId ? { ...t, name: updatedTrack.name } : t));
      dispatch(setTracks(updatedTracks));
      setEditingTrackId(null);
    } catch (err: any) {
      console.error('Failed to rename track:', err);
      ui.alert(err.message || 'Failed to rename track', 'Rename Failed');
    }
  };

  const handleDeleteRenderClick = async (jobId: string) => {
    const confirmDelete = await ui.confirm(
      'Are you sure you want to delete this rendered video file? This action will permanently remove the video from storage and cannot be undone.',
      'Delete Rendered Video?',
      'danger'
    );
    if (!confirmDelete) return;

    try {
      await apiDelete(`/api/render/${jobId}`);
      dispatch(removeRenderJob(jobId));
      ui.toast('Rendered video deleted successfully', 'success');

      // Update quota limits after deletion
      const userProfile = await apiGet('/api/auth/profile');
      if (userProfile?.quota) {
        setQuota(userProfile.quota);
      }
    } catch (err: any) {
      console.error('Failed to delete render job:', err);
      ui.alert(err.message || 'Failed to delete render job', 'Error');
    }
  };

  const handleSubscribe = async (plan: 'starter' | 'pro' | 'expert' | 'ultra', interval: 'month' | 'year') => {
    setLoadingBilling(true);
    try {
      const data = await apiPost('/api/stripe/checkout-session', { plan, interval });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout session URL returned');
      }
    } catch (err: any) {
      console.error('Failed to create checkout session:', err);
      ui.alert(err.message || 'Failed to initialize payment checkout.', 'Payment Error');
    } finally {
      setLoadingBilling(false);
    }
  };

  const handleManageBilling = async (plan?: string | unknown, interval?: 'month' | 'year') => {
    const cleanPlan = typeof plan === 'string' ? plan : undefined;
    const cleanInterval = typeof interval === 'string' ? interval : undefined;
    setLoadingBilling(true);
    try {
      const data = await apiPost('/api/stripe/customer-portal', { plan: cleanPlan, interval: cleanInterval });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No billing portal URL returned');
      }
    } catch (err: any) {
      console.error('Failed to create customer portal session:', err);
      ui.alert(err.message || 'Failed to redirect to billing management.', 'Billing Error');
    } finally {
      setLoadingBilling(false);
    }
  };

  useEffect(() => {
    if (location.state?.toastMessage) {
      ui.toast(location.state.toastMessage, 'success');
      // Clear history state
      window.history.replaceState({}, document.title);
    }
  }, [location.state, ui]);

  const fetchProfileAndQuota = async () => {
    try {
      const data = await apiGet('/api/auth/profile');
      if (data?.user) {
        dispatch(updateUser(data.user));
      }
      if (data?.quota) {
        setQuota(data.quota);
      }
    } catch (err: any) {
      console.error('Failed to load user profile or quota:', err);
    }
  };

  const fetchTracks = async (pageNumber: number, append = false) => {
    if (!token) return;
    if (pageNumber === 1 && !append) {
      setLoading(true);
    } else {
      setFetchingMore(true);
    }
    setError(null);

    try {
      const queryParams = new URLSearchParams();
      queryParams.append('page', pageNumber.toString());
      queryParams.append('limit', '12');
      if (debouncedSearch.trim()) {
        queryParams.append('search', debouncedSearch.trim());
      }
      if (selectedActivityType !== 'all') {
        queryParams.append('activityType', selectedActivityType);
      }

      if (selectedDistanceRange !== 'all') {
        let minDis = 0;
        let maxDis = Infinity;
        if (selectedDistanceRange === '<10') {
          maxDis = 10000;
        } else if (selectedDistanceRange === '10-25') {
          minDis = 10000;
          maxDis = 25000;
        } else if (selectedDistanceRange === '25-50') {
          minDis = 25000;
          maxDis = 50000;
        } else if (selectedDistanceRange === '50-100') {
          minDis = 50000;
          maxDis = 100000;
        } else if (selectedDistanceRange === '100+') {
          minDis = 100000;
        }
        queryParams.append('minDistance', minDis.toString());
        if (maxDis !== Infinity) {
          queryParams.append('maxDistance', maxDis.toString());
        }
      }

      if (selectedDateRange !== 'all') {
        const now = new Date();
        const minDate = new Date();
        if (selectedDateRange === '7days') {
          minDate.setDate(now.getDate() - 7);
        } else if (selectedDateRange === '30days') {
          minDate.setDate(now.getDate() - 30);
        } else if (selectedDateRange === 'year') {
          minDate.setMonth(0);
          minDate.setDate(1);
          minDate.setHours(0, 0, 0, 0);
        }
        queryParams.append('minDate', minDate.toISOString());
      }

      queryParams.append('sortBy', sortByField);
      queryParams.append('sortOrder', sortOrderDirection);

      const response = await apiGet(`/api/tracks?${queryParams.toString()}`);

      if (append) {
        dispatch(setTracks([...tracks, ...response.data]));
      } else {
        dispatch(setTracks(response.data));
      }
      setHasMore(response.page < response.totalPages);
      setTotalCount(response.total);
      setAggregates(response.aggregates);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch tracks');
    } finally {
      setLoading(false);
      setFetchingMore(false);
    }
  };

  const fetchRenders = async () => {
    if (!token) return;
    try {
      const data = await apiGet('/api/render');
      dispatch(setRenders(data));
    } catch (err) {
      console.error('Failed to load render jobs:', err);
    }
  };

  const fetchSyncs = async () => {
    if (!token) return;
    try {
      const data = await apiGet('/api/integrations/sync/status');
      dispatch(setSyncs(data));
    } catch (err) {
      console.error('Failed to load active syncs:', err);
    }
  };

  const handleDismissSync = async (provider: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await apiPost(`/api/integrations/sync/${provider}/dismiss`);
      dispatch(removeSyncStatus(provider));
    } catch (err) {
      console.error('Failed to dismiss sync status:', err);
    }
  };

  const isFirstRender = useRef(true);
  const scrollRestoredRef = useRef(false);
  const prevStravaSyncStatusRef = useRef<string | undefined>(undefined);

  // Sync pagination/aggregates metadata to sessionStorage
  useEffect(() => {
    if (aggregates) {
      sessionStorage.setItem('library_aggregates', JSON.stringify(aggregates));
    } else {
      sessionStorage.removeItem('library_aggregates');
    }
  }, [aggregates]);

  useEffect(() => {
    sessionStorage.setItem('library_totalCount', totalCount.toString());
  }, [totalCount]);

  useEffect(() => {
    sessionStorage.setItem('library_hasMore', hasMore.toString());
  }, [hasMore]);

  useEffect(() => {
    sessionStorage.setItem('library_page', page.toString());
  }, [page]);

  // Monitor window scroll and save it to sessionStorage
  useEffect(() => {
    const handleScroll = () => {
      if (scrollRestoredRef.current) {
        sessionStorage.setItem('library_scrollPosition', window.scrollY.toString());
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Restore scroll position after tracks are loaded (once per mount)
  useEffect(() => {
    if (tracks.length > 0 && !scrollRestoredRef.current) {
      const savedScroll = sessionStorage.getItem('library_scrollPosition');
      if (savedScroll) {
        const scrollY = parseInt(savedScroll, 10);
        if (scrollY > 0) {
          let attempts = 0;
          const maxAttempts = 20; // 1 second max (20 * 50ms)
          let lastScrollY = -1;
          let sameCount = 0;

          const interval = setInterval(() => {
            window.scrollTo({ top: scrollY, behavior: 'instant' as any });
            const currentScrollY = window.scrollY;
            attempts++;

            if (currentScrollY === lastScrollY) {
              sameCount++;
            } else {
              sameCount = 0;
            }
            lastScrollY = currentScrollY;

            // Stop if:
            // 1. We reached/almost reached the target scroll Y
            // 2. We reached max attempts limit
            // 3. The scroll position remained unchanged for 3 checks (layout is stable and scroll height is capped)
            if (
              Math.abs(currentScrollY - scrollY) < 5 ||
              attempts >= maxAttempts ||
              sameCount >= 3
            ) {
              clearInterval(interval);
              scrollRestoredRef.current = true;
            }
          }, 50);

          return () => clearInterval(interval);
        }
      }
      scrollRestoredRef.current = true;
    }
  }, [tracks]);

  // Trigger search / filter changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // Save filters to sessionStorage whenever they change
    sessionStorage.setItem('library_search', searchQuery);
    sessionStorage.setItem('library_activityType', selectedActivityType);
    sessionStorage.setItem('library_distanceRange', selectedDistanceRange);
    sessionStorage.setItem('library_dateRange', selectedDateRange);
    sessionStorage.setItem('library_sortBy', sortByField);
    sessionStorage.setItem('library_sortOrder', sortOrderDirection);

    if (isFirstRender.current) {
      isFirstRender.current = false;
      // Skip initial fetch if we already have tracks in Redux
      if (tracks.length === 0 && token) {
        setPage(1);
        fetchTracks(1, false);
      }
      return;
    }

    if (token) {
      // Clear saved scroll position on explicit filter changes
      sessionStorage.setItem('library_scrollPosition', '0');
      setPage(1);
      fetchTracks(1, false);
    }
  }, [
    token,
    debouncedSearch,
    selectedActivityType,
    selectedDistanceRange,
    selectedDateRange,
    sortByField,
    sortOrderDirection,
  ]);

  // Load other initial data
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (token) {
      fetchProfileAndQuota();
      fetchRenders();
      fetchSyncs();
      fetchConnections();
    }
  }, [token]);

  // Open Settings Modal on billing tab when query param settings=billing is present
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('settings') === 'billing') {
      setShowSettingsModal(true);
      setSettingsActiveTab('billing');

      // Instantly trigger Stripe subscription check in the backend
      apiPost('/api/stripe/sync', {})
        .then(() => fetchProfileAndQuota())
        .catch((err) => console.error('Failed to trigger instant sync:', err));

      // Clean up URL query parameters
      navigate('/library', { replace: true });
    }
  }, [location.search, navigate]);

  // IntersectionObserver for Infinite Scroll
  const observerTarget = useRef<HTMLDivElement>(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!hasMore || loading || fetchingMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          const nextPage = page + 1;
          setPage(nextPage);
          fetchTracks(nextPage, true);
        }
      },
      { threshold: 0.1 },
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => observer.disconnect();
  }, [hasMore, page, loading, fetchingMore, tracks, fetchTracks]);

  useEffect(() => {
    if (stravaSync) {
      const prevStatus = prevStravaSyncStatusRef.current;
      if (stravaSync.status !== prevStatus) {
        // Only trigger toast notifications if transitioning from an active running sync state
        const wasSyncing = prevStatus === 'fetching' || prevStatus === 'syncing';
        if (wasSyncing) {
          if (stravaSync.status === 'completed') {
            fetchTracks(1, false);
            fetchConnections();
            ui.toast('Strava activities successfully synchronized!', 'success');
          } else if (stravaSync.status === 'failed') {
            fetchConnections();
            ui.toast(`Strava sync failed: ${stravaSync.error || 'Unknown error'}`, 'error');
          } else if ((stravaSync.status as any) === 'paused') {
            fetchConnections();
            const resumeTime = (stravaSync as any).resumeAt ? new Date((stravaSync as any).resumeAt).toLocaleTimeString() : 'later';
            ui.toast(`Strava sync paused (rate limit hit). Will resume at ${resumeTime}.`, 'warning');
          }
        }
        prevStravaSyncStatusRef.current = stravaSync.status;
      }
    } else {
      prevStravaSyncStatusRef.current = undefined;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stravaSync]);

  const handleDeleteClick = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const confirmDelete = await ui.confirm(
      'Are you sure you want to delete this track? This will permanently remove the track and all associated rendered flyover videos.',
      'Delete Activity?',
      'danger'
    );
    if (!confirmDelete) return;

    try {
      await apiDelete(`/api/tracks/${id}`);
      dispatch(removeTrack(id));
      ui.toast('Track and associated videos deleted successfully', 'success');
      // Refresh renders list to clear deleted items
      fetchRenders();
    } catch (err: any) {
      ui.alert(err.message || 'Failed to delete track', 'Error');
    }
  };

  const handleLogout = async () => {
    try {
      await apiPost('/api/auth/logout');
    } catch (err) {
      console.error('Logout request failed:', err);
    } finally {
      sessionStorage.clear();
      dispatch(logout());
      navigate('/login');
    }
  };

  const handleDisconnectStrava = async () => {
    const confirmDisconnect = await ui.confirm(
      'Are you sure you want to disconnect your Strava account? You will not be able to sync new activities until you reconnect.',
      'Disconnect Strava Account?',
      'danger'
    );
    if (!confirmDisconnect) return;

    try {
      await apiDelete('/api/integrations/disconnect/strava');
      await fetchConnections();
      ui.toast('Disconnected from Strava', 'success');
    } catch (err: any) {
      console.error(err);
      ui.alert(err.message || 'Failed to disconnect Strava account', 'Error');
    }
  };

  const getActivityEmoji = (type: string) => {
    switch (type) {
      case 'running':
        return '🏃';
      case 'cycling':
        return '🚴';
      case 'hiking':
        return '🥾';
      case 'climbing':
        return '🧗';
      default:
        return '📦';
    }
  };

  const formatDistance = (meters: number) => {
    return formatGlobalDistance(meters, user?.units || 'metric');
  };

  const formatElevation = (meters: number) => {
    return formatGlobalElevation(meters, user?.units || 'metric');
  };

  const formatDuration = (seconds: number) => {
    return formatGlobalDuration(seconds);
  };

  const formatDate = (isoString: string) => {
    return formatGlobalDate(isoString, user?.dateFormat || 'YYYY-MM-DD');
  };

  const formatSpeedOrPace = (speedMps: number, activityType: string) => {
    return formatGlobalSpeedOrPace(speedMps, activityType, user?.units || 'metric');
  };

  const renderJobsList = Object.values(renders);
  const syncsList = Object.values(syncs);
  const activeJobs = renderJobsList.filter((job) => job.status === 'queued' || job.status === 'rendering');
  const completedJobs = renderJobsList.filter((job) => job.status === 'completed');
  const activeSyncs = syncsList.filter((sync) => sync.status === 'fetching' || sync.status === 'syncing');
  const activeCount = activeJobs.length + activeSyncs.length;

  const combinedNotifications = [
    ...renderJobsList.map((job) => ({ ...job, type: 'render' as const, id: job.jobId })),
    ...syncsList.map((sync) => ({ ...sync, type: 'sync' as const, id: sync.provider })),
  ];

  const isNotificationActive = (item: any) => {
    return (
      item.status === 'queued' || item.status === 'rendering' || item.status === 'fetching' || item.status === 'syncing'
    );
  };

  combinedNotifications.sort((a, b) => {
    const aActive = isNotificationActive(a);
    const bActive = isNotificationActive(b);
    if (aActive && !bActive) return -1;
    if (!aActive && bActive) return 1;
    return 0;
  });

  // Bulk Actions Handlers
  const isSelected = (id: string) => selectedTrackIds.includes(id);

  const toggleSelect = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (selectedTrackIds.includes(id)) {
      setSelectedTrackIds((prev) => prev.filter((tId) => tId !== id));
    } else {
      setSelectedTrackIds((prev) => [...prev, id]);
    }
  };

  const handleSelectAll = () => {
    setSelectedTrackIds(tracks.map((t) => t.id));
  };

  const handleClearSelection = () => {
    setSelectedTrackIds([]);
  };

  const handleBulkDelete = async () => {
    if (selectedTrackIds.length === 0) return;
    const confirmDelete = await ui.confirm(
      `Are you sure you want to delete the ${selectedTrackIds.length} selected activities? This will permanently delete the tracks and all associated rendered flyover videos. This action cannot be undone.`,
      `Delete ${selectedTrackIds.length} Activities?`,
      'danger'
    );
    if (!confirmDelete) return;

    try {
      setLoading(true);
      await Promise.all(selectedTrackIds.map((id) => apiDelete(`/api/tracks/${id}`)));
      ui.toast(`${selectedTrackIds.length} tracks deleted successfully.`, 'success');
      setSelectedTrackIds([]);
      // Reset to page 1
      setPage(1);
      fetchTracks(1, false);
      fetchRenders();
    } catch (err: any) {
      ui.alert(err.message || 'Failed to bulk delete tracks', 'Error');
    } finally {
      setLoading(false);
    }
  };

  const openSettings = (tab: 'profile' | 'accounts' | 'activities' | 'billing') => {
    setSettingsActiveTab(tab);
    setShowSettingsModal(true);
  };

  return (
    <div
      style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: '#0b0f19', color: '#f3f4f6' }}
    >
      <Header
        user={user}
        tracks={tracks}
        activeCount={activeCount}
        combinedNotifications={combinedNotifications}
        connections={connections}
        syncingStrava={syncingStrava}
        getFullVideoUrl={getFullVideoUrl}
        setPlayingVideoUrl={setPlayingVideoUrl}
        handleLogout={handleLogout}
        handleStravaSync={handleStravaSync}
        handleStravaConnect={handleStravaConnect}
        handleDismissSync={handleDismissSync}
        openSettings={openSettings}
      />

      <QuotaBar quota={quota} />

      {/* Main Content */}
      <main className="container" style={{ flex: 1, padding: '32px 24px' }}>
        {error && (
          <div
            style={{
              padding: '14px',
              background: 'rgba(239, 68, 68, 0.08)',
              border: '1px solid rgba(239, 68, 68, 0.15)',
              borderRadius: '12px',
              color: 'var(--status-error)',
              marginBottom: '24px',
              fontSize: '14px',
            }}
          >
            {error}
          </div>
        )}

        <FiltersPanel
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          selectedActivityType={selectedActivityType}
          setSelectedActivityType={setSelectedActivityType}
          selectedDistanceRange={selectedDistanceRange}
          setSelectedDistanceRange={setSelectedDistanceRange}
          selectedDateRange={selectedDateRange}
          setSelectedDateRange={setSelectedDateRange}
          sortByField={sortByField}
          setSortByField={setSortByField}
          sortOrderDirection={sortOrderDirection}
          setSortOrderDirection={setSortOrderDirection}
          getActivityEmoji={getActivityEmoji}
        />

        {/* Dashboard Aggregate Stats Banner */}
        {aggregates && (
          <div
            className="glass-panel"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: '16px',
              padding: '16px 24px',
              borderRadius: '16px',
              border: '1px solid rgba(255,255,255,0.08)',
              marginBottom: '24px',
              background: 'rgba(15, 23, 42, 0.4)',
              backdropFilter: 'blur(12px)',
            }}
          >
            <div style={{ textAlign: 'center' }}>
              <span
                style={{
                  fontSize: '11px',
                  color: 'var(--text-muted)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  fontWeight: 600,
                  display: 'block',
                  marginBottom: '4px',
                }}
              >
                Total Distance
              </span>
              <strong style={{ fontSize: '20px', fontWeight: 700, color: '#fff' }}>
                {formatDistance(aggregates.totalDistance)}
              </strong>
            </div>
            <div style={{ textAlign: 'center' }}>
              <span
                style={{
                  fontSize: '11px',
                  color: 'var(--text-muted)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  fontWeight: 600,
                  display: 'block',
                  marginBottom: '4px',
                }}
              >
                Total Elevation Gain
              </span>
              <strong style={{ fontSize: '20px', fontWeight: 700, color: '#fff' }}>
                +{formatElevation(aggregates.totalElevationGain)}
              </strong>
            </div>
            <div style={{ textAlign: 'center' }}>
              <span
                style={{
                  fontSize: '11px',
                  color: 'var(--text-muted)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  fontWeight: 600,
                  display: 'block',
                  marginBottom: '4px',
                }}
              >
                Total Duration
              </span>
              <strong style={{ fontSize: '20px', fontWeight: 700, color: '#fff' }}>
                {formatDuration(aggregates.totalDuration)}
              </strong>
            </div>
            <div style={{ textAlign: 'center' }}>
              <span
                style={{
                  fontSize: '11px',
                  color: 'var(--text-muted)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  fontWeight: 600,
                  display: 'block',
                  marginBottom: '4px',
                }}
              >
                Total Activities
              </span>
              <strong style={{ fontSize: '20px', fontWeight: 700, color: '#fff' }}>
                {totalCount.toLocaleString('en-US')}
              </strong>
            </div>
          </div>
        )}

        {loading && tracks.length === 0 ? (
          <div className="flex-center" style={{ minHeight: '30vh' }}>
            <div className="spinner" />
          </div>
        ) : tracks.length === 0 ? (
          <div
            className="glass-panel"
            style={{
              padding: '80px 40px',
              textAlign: 'center',
              maxWidth: '600px',
              margin: '40px auto',
              border: '1px solid rgba(255,255,255,0.05)',
            }}
          >
            <MapIcon size={48} style={{ color: 'var(--text-muted)', marginBottom: '20px' }} />
            <h2 style={{ fontSize: '24px', fontWeight: 800, marginBottom: '8px', letterSpacing: '-0.02em' }}>
              No activities found
            </h2>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '32px', fontSize: '15px', lineHeight: 1.6 }}>
              Try adjusting your search query or filters, or upload a new track to get started.
            </p>
            <button
              onClick={() => navigate('/upload')}
              className="btn btn-primary"
              style={{ padding: '12px 28px', borderRadius: '24px' }}
            >
              <Plus size={18} />
              <span>Upload Track</span>
            </button>
          </div>
        ) : (
          <div>
            <div
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}
            >
              <div>
                <h2 style={{ fontSize: '22px', fontWeight: 800, margin: 0, letterSpacing: '-0.02em' }}>
                  Activities Library
                </h2>
                <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginTop: '2px' }}>
                  Choose a track to customize, render, or download flyovers
                </p>
              </div>
            </div>

            {/* Track Card Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '24px' }}>
              {tracks.map((track) => (
                <TrackCard
                  key={track.id}
                  track={track}
                  isSelected={isSelected(track.id)}
                  hoveredTrackId={hoveredTrackId}
                  setHoveredTrackId={setHoveredTrackId}
                  toggleSelect={toggleSelect}
                  editingTrackId={editingTrackId}
                  editingName={editingName}
                  setEditingName={setEditingName}
                  startEditing={startEditing}
                  cancelEditing={cancelEditing}
                  saveName={saveName}
                  formatDate={formatDate}
                  formatDistance={formatDistance}
                  formatDuration={formatDuration}
                  formatSpeedOrPace={formatSpeedOrPace}
                  getActivityEmoji={getActivityEmoji}
                  completedJobs={completedJobs}
                  activeJobs={activeJobs}
                  setPlayingVideoUrl={setPlayingVideoUrl}
                  getFullVideoUrl={getFullVideoUrl}
                  handleDeleteRenderClick={handleDeleteRenderClick}
                  handleDeleteClick={handleDeleteClick}
                  navigate={navigate}
                />
              ))}
            </div>

            {/* Target element for Infinite Scroll IntersectionObserver */}
            {hasMore && (
              <div ref={observerTarget} style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
                <div className="spinner" style={{ width: '28px', height: '28px', borderWidth: '2.5px' }} />
              </div>
            )}

            {fetchingMore && !hasMore && (
              <div style={{ textAlign: 'center', padding: '20px 0', color: 'var(--text-muted)', fontSize: '13px' }}>
                Loading...
              </div>
            )}
          </div>
        )}

        {/* Floating Bulk Actions Bar */}
        {selectedTrackIds.length > 0 && (
          <div
            style={{
              position: 'fixed',
              bottom: '24px',
              left: '50%',
              transform: 'translateX(-50%)',
              background: 'rgba(15, 23, 42, 0.9)',
              backdropFilter: 'blur(16px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '40px',
              padding: '12px 28px',
              display: 'flex',
              alignItems: 'center',
              gap: '20px',
              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.7)',
              zIndex: 500,
              animation: 'slideUp 0.3s ease-out',
            }}
          >
            <span style={{ fontSize: '14px', fontWeight: 700, color: '#fff' }}>
              {selectedTrackIds.length} activities selected
            </span>
            <div style={{ height: '20px', width: '1px', background: 'rgba(255, 255, 255, 0.15)' }} />
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={handleSelectAll}
                className="btn btn-secondary"
                style={{ padding: '6px 14px', fontSize: '12px', borderRadius: '20px' }}
              >
                Select All
              </button>
              <button
                onClick={handleClearSelection}
                className="btn btn-secondary"
                style={{ padding: '6px 14px', fontSize: '12px', borderRadius: '20px' }}
              >
                Clear
              </button>
              <button
                onClick={handleBulkDelete}
                className="btn btn-danger"
                style={{
                  padding: '6px 14px',
                  fontSize: '12px',
                  borderRadius: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                <Trash2 size={13} />
                <span>Delete Selected</span>
              </button>
            </div>
          </div>
        )}

        <style>{`
          @keyframes slideUp {
            from { transform: translate(-50%, 80px); opacity: 0; }
            to { transform: translate(-50%, 0); opacity: 1; }
          }
        `}</style>
      </main>

      {/* Video Player Modal */}
      <Modal isOpen={!!playingVideoUrl} onClose={() => setPlayingVideoUrl(null)} maxWidth="960px">
        <div
          style={{
            position: 'relative',
            width: '100%',
            background: '#0f172a',
            borderRadius: '16px',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '14px 20px',
              borderBottom: '1px solid rgba(255,255,255,0.06)',
            }}
          >
            <span style={{ fontWeight: 700, fontSize: '16px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Video size={16} style={{ color: 'var(--accent-primary)' }} />
              <span>Render Preview</span>
            </span>
            <button
              onClick={() => setPlayingVideoUrl(null)}
              style={{
                background: 'rgba(255,255,255,0.05)',
                border: 'none',
                borderRadius: '50%',
                color: '#fff',
                width: '28px',
                height: '28px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
              }}
            >
              <X size={16} />
            </button>
          </div>

          <div style={{ width: '100%', aspectRatio: '16/9', background: '#000' }}>
            {playingVideoUrl && (
              <video
                src={getFullVideoUrl(playingVideoUrl)}
                controls
                autoPlay
                style={{ width: '100%', height: '100%', display: 'block' }}
              />
            )}
          </div>

          <div
            style={{
              padding: '14px 20px',
              display: 'flex',
              justifyContent: 'flex-end',
              background: 'rgba(255,255,255,0.01)',
            }}
          >
            <a
              href={getFullVideoUrl(playingVideoUrl)}
              download
              className="btn btn-primary"
              style={{ padding: '8px 20px', fontSize: '13px', borderRadius: '24px' }}
            >
              <Download size={14} />
              <span>Download Video</span>
            </a>
          </div>
        </div>
      </Modal>

      <SettingsModal
        isOpen={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
        user={user}
        connections={connections}
        syncingStrava={syncingStrava}
        handleStravaConnect={handleStravaConnect}
        handleStravaSync={handleStravaSync}
        setShowDisconnectConfirm={handleDisconnectStrava}
        billingInterval={billingInterval}
        setBillingInterval={setBillingInterval}
        loadingBilling={loadingBilling}
        handleSubscribe={handleSubscribe}
        handleManageBilling={handleManageBilling}
        settingsActiveTab={settingsActiveTab}
        setSettingsActiveTab={setSettingsActiveTab}
      />
    </div>
  );
};
